/**
 * ScriptRunner — Execute scripts in an isolated tsx subprocess.
 *
 * The subprocess approach replaces the earlier `new Function()` eval and gives us:
 *   - Real sandbox (separate process, SIGKILL on timeout).
 *   - Real TypeScript (tsx transpiles on the fly).
 *   - npm imports available to scripts (clustering, stats, geo, etc.).
 *
 * Protocol: NDJSON over the child's stdin/stdout. See script-ipc.ts + backend/docs/SCRIPT-FLOW-IMPLEMENTATION.md.
 */

import { spawn, type ChildProcessWithoutNullStreams } from 'child_process';
import * as path from 'path';
import * as os from 'os';
import type { ExternalTool } from '../services/tool-executor-service';
import type { StreamBuffer } from '../stream-buffer';
import type { ScriptRecipe, ScriptResult, ScriptQueryResult } from './types';
import {
	encodeMessage,
	LineSplitter,
	type ChildToParentMessage,
	type ExecutedQuery,
} from './script-ipc';
import { logger } from '../../utils/logger';
import { formatQueryResultForLLM } from '../llm-result-truncator';
import { STREAM_PREVIEW_MAX_ROWS, STREAM_PREVIEW_MAX_CHARS } from '../constants';

/**
 * Wall-clock cap for a single script execution.
 *
 * Default 60s — long enough for a multi-query analytical script, short enough
 * that a hung or looping script frees its concurrency slot quickly (see #2 in
 * backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md). The previous 5-minute value let
 * slow scripts pile up faster than they drained.
 *
 * Override globally with the `SCRIPT_TIMEOUT_MS` env var, or per-call via
 * `RunScriptOptions.timeoutMs`.
 */
const DEFAULT_SCRIPT_TIMEOUT_MS = 60_000;
const SCRIPT_TIMEOUT_MS = (() => {
	const envVal = Number(process.env.SCRIPT_TIMEOUT_MS);
	return Number.isFinite(envVal) && envVal > 0 ? envVal : DEFAULT_SCRIPT_TIMEOUT_MS;
})();

// ============================================
// Concurrency cap (#2)
// ============================================
// Every runScript() spawns a full node+tsx process. With no cap, inbound load
// translates 1:1 into spawned processes — a self-inflicted fork-bomb that
// exhausts RAM/PIDs/CPU. The semaphore bounds how many run at once; excess
// callers queue, and past a hard queue ceiling we shed (reject) rather than
// pile up an unbounded backlog. See backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md #2.

const MAX_CONCURRENT_SCRIPTS = (() => {
	const envVal = Number(process.env.SCRIPT_MAX_CONCURRENCY);
	if (Number.isFinite(envVal) && envVal > 0) return Math.floor(envVal);
	return Math.max(2, Math.min(8, (os.cpus()?.length || 4) - 1));
})();

const MAX_SCRIPT_QUEUE = (() => {
	const envVal = Number(process.env.SCRIPT_MAX_QUEUE);
	if (Number.isFinite(envVal) && envVal >= 0) return Math.floor(envVal);
	return 100;
})();

/** Thrown by the semaphore when the wait queue is full — caller sheds the load. */
export class ScriptCapacityError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'ScriptCapacityError';
	}
}

/**
 * Counting semaphore with a bounded wait queue. A released slot is handed
 * directly to the next waiter (so `active` only changes when a slot is taken
 * from / returned to the free pool, never on transfer).
 */
class Semaphore {
	private active = 0;
	private waiters: Array<() => void> = [];

	constructor(private readonly max: number, private readonly maxQueue: number) {}

	async acquire(): Promise<void> {
		if (this.active < this.max) {
			this.active++;
			return;
		}
		if (this.waiters.length >= this.maxQueue) {
			throw new ScriptCapacityError(
				`Script execution at capacity (${this.max} running, ${this.waiters.length} queued). Try again shortly.`,
			);
		}
		return new Promise<void>((resolve) => this.waiters.push(resolve));
	}

	release(): void {
		const next = this.waiters.shift();
		if (next) next();          // transfer the slot to the next waiter (active unchanged)
		else this.active--;        // no waiter — return the slot to the pool
	}

	get queued(): number { return this.waiters.length; }
}

const scriptSemaphore = new Semaphore(MAX_CONCURRENT_SCRIPTS, MAX_SCRIPT_QUEUE);
logger.info(`[ScriptRunner] Concurrency cap: ${MAX_CONCURRENT_SCRIPTS} concurrent, queue ${MAX_SCRIPT_QUEUE}`);

/**
 * Lazily resolved path to the `tsx` CLI binary. Resolved once at first use
 * so we don't pay `require.resolve` on every execution.
 */
let tsxBinaryPath: string | null = null;
function resolveTsxBinary(): string {
	if (tsxBinaryPath) return tsxBinaryPath;
	try {
		// tsx exposes its CLI via a distinct export — find the package root and look there
		const tsxPkg = require.resolve('tsx/package.json');
		const tsxDir = path.dirname(tsxPkg);
		// tsx 4.x ships the CLI as ./dist/cli.mjs
		tsxBinaryPath = path.join(tsxDir, 'dist', 'cli.mjs');
		return tsxBinaryPath;
	} catch (err) {
		throw new Error(
			`Could not resolve tsx binary. Install tsx as a dependency. Underlying error: ${(err as Error).message}`,
		);
	}
}

/**
 * Path to the bootstrap file that runs inside the child process.
 * Resolved relative to __dirname — when tsup bundles `index.ts`, this file
 * lives at dist/index.js and the bootstrap is emitted alongside at
 * dist/userResponse/scripts/script-bootstrap.js (mirroring the src tree).
 */
function resolveBootstrapPath(): string {
	// In CJS output, __dirname is available. In ESM output, tsup shims it.
	return path.resolve(__dirname, 'userResponse/scripts/script-bootstrap.js');
}

// ============================================
// Public API
// ============================================

export interface RunScriptOptions {
	/** Data sources the script is allowed to query via ctx.query */
	externalTools: ExternalTool[];
	/** Optional — for propagating per-query UI progress to the user */
	streamBuffer?: StreamBuffer;
	/** Override the wall-clock timeout (default `SCRIPT_TIMEOUT_MS`, 60s). */
	timeoutMs?: number;
	/**
	 * Per-turn cancellation signal. When the user hits "Stop" mid-run, the child
	 * process group is SIGKILLed and the run resolves as an aborted failure (the
	 * caller is already unwinding, so the result is discarded).
	 */
	signal?: AbortSignal;
}

/**
 * Execute a recipe by spawning a tsx child on the script's .ts file.
 * `scriptPath` is the absolute path to the saved `.ts` body.
 */
export async function runScript(
	recipe: ScriptRecipe,
	scriptPath: string,
	params: Record<string, any>,
	options: RunScriptOptions,
): Promise<ScriptResult> {
	const startedAt = Date.now();
	const resolvedParams = coerceParams(recipe, applyDefaults(recipe, params));
	const paramSchema = buildParamSchema(recipe);

	logger.info(`[ScriptRunner] Executing "${recipe.name}" (${recipe.id}) with params: ${JSON.stringify(resolvedParams)}`);

	// Concurrency gate (#2) — bounds live subprocesses; sheds under sustained
	// overload. A capacity rejection returns a normal failure ScriptResult so the
	// caller falls through to the agent flow (which spawns no subprocess) instead
	// of crashing or piling up.
	try {
		if (scriptSemaphore.queued > 0) {
			logger.info(`[ScriptRunner] "${recipe.name}" queued (${scriptSemaphore.queued} ahead)`);
		}
		await scriptSemaphore.acquire();
	} catch (capErr) {
		const msg = capErr instanceof Error ? capErr.message : String(capErr);
		logger.warn(`[ScriptRunner] "${recipe.name}" shed: ${msg}`);
		return {
			success: false,
			data: [],
			executedQueries: [],
			error: msg,
			executionTimeMs: Date.now() - startedAt,
		};
	}

	try {
		const result = await executeInSubprocess(
			scriptPath,
			resolvedParams,
			paramSchema,
			options.externalTools,
			options.streamBuffer,
			options.timeoutMs ?? SCRIPT_TIMEOUT_MS,
			options.signal,
		);

		const totalMs = Date.now() - startedAt;

		if (result.kind === 'ok') {
			// Enrich each executedQuery with the real source name (child only had toolId)
			const executedQueries = enrichQueries(result.executedQueries, options.externalTools);
			logger.info(`[ScriptRunner] "${recipe.name}" succeeded — ${result.data.length} rows in ${totalMs}ms`);
			return {
				success: true,
				data: result.data,
				executedQueries,
				executionTimeMs: totalMs,
			};
		}

		const executedQueries = enrichQueries(result.executedQueries, options.externalTools);
		logger.error(`[ScriptRunner] "${recipe.name}" failed in ${totalMs}ms [${result.phase}]: ${result.message}`);
		return {
			success: false,
			data: [],
			executedQueries,
			error: result.message,
			errorPhase: result.phase,
			executionTimeMs: totalMs,
		};

	} catch (err) {
		const totalMs = Date.now() - startedAt;
		const msg = err instanceof Error ? err.message : String(err);
		logger.error(`[ScriptRunner] "${recipe.name}" crashed in ${totalMs}ms: ${msg}`);
		return {
			success: false,
			data: [],
			executedQueries: [],
			error: msg,
			executionTimeMs: totalMs,
		};
	} finally {
		// Always free the slot — success, failure, timeout, or crash.
		scriptSemaphore.release();
	}
}

// ============================================
// Subprocess orchestration
// ============================================

type SubprocessResult =
	| { kind: 'ok'; data: any[]; count: number; executedQueries: ExecutedQuery[] }
	| {
		kind: 'err';
		phase: 'compile' | 'runtime' | 'timeout' | 'ipc';
		message: string;
		executedQueries: ExecutedQuery[];
	};

function executeInSubprocess(
	scriptPath: string,
	params: Record<string, any>,
	paramSchema: Record<string, 'string' | 'number' | 'date' | 'date_range' | 'enum' | 'boolean'>,
	externalTools: ExternalTool[],
	streamBuffer: StreamBuffer | undefined,
	timeoutMs: number,
	signal?: AbortSignal,
): Promise<SubprocessResult> {
	return new Promise((resolve) => {
		const tsxBin = resolveTsxBinary();
		const bootstrap = resolveBootstrapPath();

		const child: ChildProcessWithoutNullStreams = spawn(
			process.execPath, // node — tsx runs via its own cli, we invoke it with node
			[tsxBin, bootstrap, scriptPath],
			{
				stdio: ['pipe', 'pipe', 'pipe'],
				// `detached: true` makes the child a process-group LEADER. tsx 4.x
				// re-execs node to run the user script, so the script is a GRANDCHILD;
				// a plain `child.kill()` would hit only the tsx wrapper and orphan the
				// grandchild (a runaway/looping script that outlives the timeout — #4).
				// With its own group we can SIGKILL the whole tree via `-pid` in
				// cleanup(). We deliberately do NOT unref() — the parent still tracks
				// the child's exit and pipes normally. (POSIX; Windows falls back to
				// a direct kill in cleanup().)
				detached: process.platform !== 'win32',
				env: {
					...process.env,
					// Keep the child quiet about dotenv etc.
					NODE_NO_WARNINGS: '1',
				},
			},
		);

		let resolved = false;
		let executedQueries: ExecutedQuery[] = [];
		let stderrBuffer = '';

		const cleanup = () => {
			if (child.killed) return;
			const pid = child.pid;
			// Kill the whole process group (negative pid) so the tsx wrapper AND the
			// user-script grandchild both die — a runaway script can't survive the
			// timeout as an orphan (#4). Falls back to a direct kill on Windows, when
			// pid is unknown, or if the group is already gone (ESRCH).
			if (pid && process.platform !== 'win32') {
				try {
					process.kill(-pid, 'SIGKILL');
					return;
				} catch { /* group gone or not a leader — fall through */ }
			}
			try { child.kill('SIGKILL'); } catch { /* ignore */ }
		};

		const finish = (r: SubprocessResult) => {
			if (resolved) return;
			resolved = true;
			clearTimeout(timer);
			if (signal && onAbort) signal.removeEventListener('abort', onAbort);
			cleanup();
			resolve(r);
		};

		const timer = setTimeout(() => {
			finish({
				kind: 'err',
				phase: 'timeout',
				message: `Script execution timed out after ${timeoutMs}ms`,
				executedQueries,
			});
		}, timeoutMs);

		// User cancellation: SIGKILL the child process group immediately and
		// resolve as an aborted failure. The caller is already unwinding the turn,
		// so this result is discarded — we just need the subprocess to die now
		// rather than run to completion in the background.
		const onAbort = () => {
			finish({
				kind: 'err',
				phase: 'runtime',
				message: 'Script execution aborted by user',
				executedQueries,
			});
		};
		if (signal) {
			if (signal.aborted) { onAbort(); return; }
			signal.addEventListener('abort', onAbort, { once: true });
		}

		// ---- stdout: parse NDJSON from child ----
		const stdoutSplitter = new LineSplitter();
		child.stdout.setEncoding('utf-8');
		child.stdout.on('data', (chunk: string) => {
			let lines: string[];
			try {
				lines = stdoutSplitter.push(chunk);
			} catch (err) {
				// Oversized/runaway child output (#9) — abort and SIGKILL the group.
				finish({
					kind: 'err',
					phase: 'ipc',
					message: err instanceof Error ? err.message : String(err),
					executedQueries,
				});
				return;
			}
			for (const line of lines) {
				let msg: ChildToParentMessage;
				try {
					msg = JSON.parse(line);
				} catch {
					continue;
				}
				handleChildMessage(msg);
			}
		});

		// ---- stderr: collect for error reporting ----
		child.stderr.setEncoding('utf-8');
		child.stderr.on('data', (chunk: string) => {
			stderrBuffer += chunk;
			// Avoid unbounded growth
			if (stderrBuffer.length > 10_000) {
				stderrBuffer = stderrBuffer.slice(-10_000);
			}
		});

		child.on('error', (err) => {
			finish({
				kind: 'err',
				phase: 'ipc',
				message: `Failed to spawn child process: ${err.message}`,
				executedQueries,
			});
		});

		child.on('exit', (code, signal) => {
			if (resolved) return;
			// Child exited without sending RESULT or ERROR. Use stderr as a clue.
			const tail = stderrBuffer.trim().split('\n').slice(-8).join('\n');
			finish({
				kind: 'err',
				phase: 'runtime',
				message: `Script child exited (code=${code}, signal=${signal}) without a result.${tail ? ' stderr: ' + tail : ''}`,
				executedQueries,
			});
		});

		// ---- send INIT to kick the child off ----
		const init = encodeMessage({
			type: 'init',
			params,
			now: new Date().toISOString(),
			paramSchema,
		});
		child.stdin.write(init);

		// ============================================
		// Handlers
		// ============================================

		function handleChildMessage(msg: ChildToParentMessage): void {
			switch (msg.type) {
				case 'stream':
					if (streamBuffer?.hasCallback()) {
						streamBuffer.write(msg.chunk);
					}
					return;

				case 'result':
					executedQueries = withSynthesizedFinalData(msg.executedQueries, msg.data, msg.count);
					finish({
						kind: 'ok',
						data: msg.data,
						count: msg.count,
						executedQueries,
					});
					return;

				case 'error':
					finish({
						kind: 'err',
						phase: msg.phase === 'compile' ? 'compile' : 'runtime',
						message: msg.message + (msg.stack ? `\n${msg.stack}` : ''),
						executedQueries,
					});
					return;
			}
		}
	});
}

// ============================================
// Tool authorization
// ============================================

function resolveAuthorizedTool(
	toolId: string,
	byId: Map<string, ExternalTool>,
	byName: Map<string, ExternalTool>,
): ExternalTool | null {
	const exact = byId.get(toolId);
	if (exact) return exact;

	const exactName = byName.get(toolId.toLowerCase());
	if (exactName) return exactName;

	// Partial ID match — kept for resilience when source IDs rotate
	for (const [id, tool] of byId) {
		if (id.includes(toolId) || toolId.includes(id)) return tool;
	}

	// Partial name match
	const lower = toolId.toLowerCase();
	for (const [name, tool] of byName) {
		if (name.includes(lower) || lower.includes(name)) return tool;
	}

	return null;
}

// ============================================
// Streaming helpers (replicated from old ScriptContext so per-query UI progress still flows)
// ============================================

function streamQueryStart(sb: StreamBuffer | undefined, sourceName: string, sql: string): void {
	if (!sb?.hasCallback()) return;
	sb.write(`\n📝 **Querying ${sourceName}:**\n\`\`\`sql\n${sql}\n\`\`\`\n\n`);
	sb.write(`__QUERY_TIMER_START_Executing query__`);
}

function streamQueryDone(sb: StreamBuffer | undefined, ms: number): void {
	if (!sb?.hasCallback()) return;
	sb.write(`__QUERY_TIMER_DONE_${(ms / 1000).toFixed(1)}__\n\n`);
}

function streamQuerySuccess(sb: StreamBuffer | undefined, sourceName: string, rows: number, total: number): void {
	if (!sb?.hasCallback()) return;
	const totalInfo = total > rows ? ` of ${total} total` : '';
	sb.write(`✅ **${rows} rows${totalInfo} from ${sourceName}**\n\n`);
}

function streamQueryError(sb: StreamBuffer | undefined, sourceName: string, msg: string): void {
	if (!sb?.hasCallback()) return;
	sb.write(`❌ **Query failed on ${sourceName}:** ${msg}\n\n`);
}

function streamRunToolStart(sb: StreamBuffer | undefined, toolName: string): void {
	if (!sb?.hasCallback()) return;
	sb.write(`\n🔧 **Running ${toolName}...**\n\n`);
	sb.write(`__QUERY_TIMER_START_Executing tool__`);
}

function streamRunToolDone(sb: StreamBuffer | undefined, toolName: string, ms: number): void {
	if (!sb?.hasCallback()) return;
	sb.write(`__QUERY_TIMER_DONE_${(ms / 1000).toFixed(1)}__\n\n`);
	sb.write(`✅ **${toolName}** completed\n\n`);
}

function streamDataPreview(sb: StreamBuffer | undefined, data: any[]): void {
	if (!sb?.hasCallback() || data.length === 0) return;
	try {
		const preview = formatQueryResultForLLM(data, {
			maxRows: STREAM_PREVIEW_MAX_ROWS,
			maxCharsPerField: STREAM_PREVIEW_MAX_CHARS,
		});
		sb.write(`<DataTable>${JSON.stringify(preview.data)}</DataTable>\n\n`);
	} catch {
		sb.write(`_Data preview not available_\n\n`);
	}
}

// ============================================
// Result enrichment
// ============================================

/**
 * Prepend a synthetic "computed:_final" entry carrying the script's post-JS
 * returned data so the component generator and the queryCache-backed UI hydration
 * path always have something to bind to.
 *
 * Two situations where we add it:
 *   1. Script ran SQL via ctx.query and then transformed the rows in JS (e.g.,
 *      HHI contributions, weighted averages). Without the synthetic entry the
 *      LLM only sees raw SQL output and either picks the wrong columns or
 *      invents federation SQL to recompute what the script already produced.
 *   2. Script never called ctx.query at all (only ctx.runTool / pure JS). The
 *      bootstrap's executedQueries is empty; without a synthetic entry the
 *      component path falls back to MainAgent's executedTools and tries to
 *      build a queryId from `t.params.sql`, which is empty for direct tools
 *      (their params are structured, not SQL). That yields an empty-payload
 *      encrypted token and the UI renders "Query expired or not found".
 *
 * Pass-through SQL scripts (final shape === raw SQL shape) skip the synthetic
 * entry to keep the prompt lean — the raw SQL row IS the answer.
 *
 * See backend/docs/SCRIPT-FLOW-IMPLEMENTATION.md.
 */
function withSynthesizedFinalData(
	queries: ExecutedQuery[],
	finalData: any[],
	finalCount: number,
): ExecutedQuery[] {
	if (!finalData || finalData.length === 0) return queries;

	// Case 2 — no tracked queries at all (runTool-only or pure-JS script). The
	// script's return value is the only data; always surface it through the
	// script_dataset sentinel so the cached payload can hydrate the components
	// on UI replay.
	if (!queries || queries.length === 0) {
		return [{
			sourceId: 'computed:_final',
			sourceName: 'Script final dataset',
			sql: '-- script final returned data (runTool / pure JS, no ctx.query)',
			data: finalData,
			count: finalCount,
			executionTimeMs: 0,
			virtual: true,
		}];
	}

	// Case 1 — there is at least one real SQL query. Compare column sets from
	// the first SQL query's first row vs the script's final first row. If the
	// script added or changed columns, surface the final.
	const firstSqlQuery = queries.find(q => !q.virtual);
	const rawSample = firstSqlQuery?.data?.[0];
	const finalSample = finalData[0];
	if (!rawSample || !finalSample || typeof rawSample !== 'object' || typeof finalSample !== 'object') {
		return queries;
	}

	const rawCols = new Set(Object.keys(rawSample));
	const finalCols = Object.keys(finalSample);
	const transformed =
		finalCols.length !== rawCols.size ||
		finalCols.some(c => !rawCols.has(c));

	if (!transformed) return queries;

	const synthetic: ExecutedQuery = {
		sourceId: 'computed:_final',
		sourceName: 'Script final dataset',
		sql: '-- script final returned data (post-JS transforms)',
		data: finalData,
		count: finalCount,
		executionTimeMs: 0,
		virtual: true,
	};
	return [synthetic, ...queries];
}

function enrichQueries(
	queries: ExecutedQuery[],
	externalTools: ExternalTool[],
): ScriptQueryResult[] {
	const byId = new Map(externalTools.map(t => [t.id, t]));
	return queries.map(q => {
		const tool = byId.get(q.sourceId);
		return {
			sourceId: q.sourceId,
			sourceName: tool?.name ?? q.sourceName ?? q.sourceId,
			sql: q.sql,
			data: q.data,
			count: q.count,
			totalCount: q.totalCount,
			executionTimeMs: q.executionTimeMs,
			virtual: q.virtual,
		};
	});
}

// ============================================
// Param handling (preserved from the pre-subprocess runner)
// ============================================

function buildParamSchema(recipe: ScriptRecipe): Record<string, 'string' | 'number' | 'date' | 'date_range' | 'enum' | 'boolean'> {
	const schema: Record<string, any> = {};
	for (const p of recipe.parameters) {
		schema[p.name] = p.type;
	}
	return schema;
}

function applyDefaults(recipe: ScriptRecipe, params: Record<string, any>): Record<string, any> {
	const resolved = { ...params };
	for (const paramDef of recipe.parameters) {
		if (resolved[paramDef.name] === undefined && paramDef.default !== undefined) {
			resolved[paramDef.name] = paramDef.default;
		}
	}
	return resolved;
}

function coerceParams(recipe: ScriptRecipe, params: Record<string, any>): Record<string, any> {
	const coerced = { ...params };

	for (const paramDef of recipe.parameters) {
		const val = coerced[paramDef.name];
		if (val === undefined || val === null) continue;

		switch (paramDef.type) {
			case 'date': {
				if (!(val instanceof Date)) {
					const d = new Date(val);
					if (!isNaN(d.getTime())) coerced[paramDef.name] = d.toISOString();
				} else {
					coerced[paramDef.name] = val.toISOString();
				}
				break;
			}
			case 'date_range': {
				if (val && typeof val === 'object') {
					const from = (val as any).from ?? (Array.isArray(val) ? val[0] : undefined);
					const to = (val as any).to ?? (Array.isArray(val) ? val[1] : undefined);
					const fromD = from instanceof Date ? from : from != null ? new Date(from) : undefined;
					const toD = to instanceof Date ? to : to != null ? new Date(to) : undefined;
					coerced[paramDef.name] = {
						from: fromD && !isNaN(fromD.getTime()) ? fromD.toISOString() : undefined,
						to: toD && !isNaN(toD.getTime()) ? toD.toISOString() : undefined,
					};
				}
				break;
			}
			case 'number': {
				if (typeof val !== 'number') {
					const n = Number(val);
					if (!isNaN(n)) coerced[paramDef.name] = n;
				}
				break;
			}
			case 'boolean': {
				if (typeof val !== 'boolean') {
					coerced[paramDef.name] = val === 'true' || val === 1 || val === '1';
				}
				break;
			}
		}
	}

	return coerced;
}
